import { useState } from 'react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Checkbox,
  CheckboxGroup,
  Stack,
  TextArea,
} from '@customerio/pluma-components/react';

const UPGRADE_OPTIONS = [
  { value: 'more_profiles', label: 'Need more profiles' },
  { value: 'predictable_billing', label: 'Want more predictable billing / manage costs' },
  { value: 'premium_features', label: 'Interested in Premium features and integrations' },
  { value: 'more_workspaces', label: 'Need more workspaces' },
  { value: 'deliverability', label: 'Deliverability needs' },
  { value: 'hands_on_support', label: 'Want more hands-on support' },
] as const;

export interface PremiumUpgradeModalData {
  /** Summary of the conversation from the agent */
  conversationSummary: string;
}

export interface PremiumUpgradeModalResult {
  selectedOptions: string[];
  additionalContext: string;
  conversationSummary: string;
}

export function PremiumUpgradeModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<PremiumUpgradeModalData, PremiumUpgradeModalResult>) {
  const [selected, setSelected] = useState<string[]>([]);
  const [additionalContext, setAdditionalContext] = useState('');

  const hasSelection = selected.length > 0;

  function handleSubmit() {
    const selectedLabels = selected.map(
      (value) => UPGRADE_OPTIONS.find((option) => option.value === value)?.label ?? value,
    );

    onConfirm({
      selectedOptions: selectedLabels,
      additionalContext: additionalContext.trim(),
      conversationSummary: data.conversationSummary,
    });
  }

  return (
    <Modal state={modalState} size="md" title="Contact Account Management">
      <ModalBody>
        <Stack gap="300">
          <CheckboxGroup label="What are you looking to accomplish?" value={selected} onChange={setSelected}>
            {UPGRADE_OPTIONS.map((option) => (
              <Checkbox key={option.value} value={option.value} label={option.label} />
            ))}
          </CheckboxGroup>

          <TextArea
            label="Anything else you'd like to share?"
            placeholder="Anything else you'd like to share with the team?"
            value={additionalContext}
            onInput={(event) => setAdditionalContext(event.target.value)}
            minRows={3}
            maxRows={6}
          />
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" isDisabled={!hasSelection} onClick={handleSubmit}>
          Submit request
        </Button>
      </ModalFooter>
    </Modal>
  );
}
